import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {InputSwitch} from 'primereact/inputswitch'
import {InputText} from 'primereact/inputtext'
import {Tag} from 'primereact/tag'
import React, {forwardRef, useImperativeHandle} from 'react'
import PhoneInput from 'react-phone-input-2'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {
    notifyMethodOptions,
    severityNotification,
} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import CheckboxButton from './CheckboxButton'
import {
    additionalNotifyMethodKeys,
    notifyTimeOption,
    timeOptionKeys,
    weekDayKeys,
    weekDayOption,
} from './Constants'
import {useAdditionalNotifyMethod} from './query-keys/AdditionNotifyMethodQueryManager'
import './style/style.css'
const initialDays = [
    weekDayKeys.monday,
    weekDayKeys.tuesday,
    weekDayKeys.wednesday,
    weekDayKeys.thursday,
    weekDayKeys.friday,
    weekDayKeys.saturday,
    weekDayKeys.sunday,
]

const initialData = {
    [additionalNotifyMethodKeys.active]: true,
    [additionalNotifyMethodKeys.notifyMethodType]: ['sms', 'email'],
    [additionalNotifyMethodKeys.time]: timeOptionKeys.allDay,
    [additionalNotifyMethodKeys.day]: initialDays,
    [additionalNotifyMethodKeys.startTime]: '00:00',
    [additionalNotifyMethodKeys.endTime]: '23:59',
    // [additionalNotifyMethodKeys.phoneInput]: '44',
}
const StyledNotifyMethodsForm = forwardRef(({hospitalId, ...props}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {user} = useAuth()
    const {currentHospital} = useUserHospital()
    const {timezoneId, userRole} = useAppConfig()
    const data = useSignal({...initialData})
    const queryClient = useQueryClient()
    const {mutateAsync: createOrUpdateAdditionalNotifyMethod} =
        useAdditionalNotifyMethod(
            !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
            user?.id,
        )

    const {mutateAsync: deleteByIdAdditionalNotifyMethod} =
        useAdditionalNotifyMethod(QueryCRUDTypes.delete, user?.id)

    const visible = useSignal(false)

    const handleShowForm = showData => {
        if (showData) {
            data.value = {
                ...showData,
            }
        } else {
            data.value = {...initialData}
        }

        visible.value = true
        blockOverflownScroll()
    }

    const handleHideForm = () => {
        // data.value = {}
        unblockOverflownScroll()
        visible.value = false
    }

    const handleOnSubmit = async () => {
        const toPostData = {
            ...data.value,
            hospitalId:
                userRole === SystemRole.ADMIN
                    ? hospitalId
                    : currentHospital?.id,
        }
        const res = await createOrUpdateAdditionalNotifyMethod(toPostData)
        if (res?.id) {
            const {additionalNotifyMethodKeys} = QueryKeyManager(
                QueryKeyTypes.additionalNotifyMethod,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: additionalNotifyMethodKeys.pattern(),
            })

            handleHideForm()
        }
    }

    const labelTemplate = (label = '', isRequired = false) => {
        return (
            <div className="flex align-items-center justify-content-between w-full mb-2">
                <div>
                    <h5 className="font-bold mb-0">{label}</h5>
                </div>
                {isRequired && (
                    <div>
                        <Tag
                            value={'This field is required !'}
                            severity="danger"
                            icon="pi pi-info-circle"
                        />
                    </div>
                )}
            </div>
        )
    }

    const handleOnChangeOtherMethods = (key, value) => {
        let holderValue = data.value[key] ? [...data.value[key]] : []
        if (isArrayAndNotEmpty(holderValue)) {
            if (holderValue.includes(value)) {
                holderValue = holderValue.filter(x => x !== value)
            } else {
                holderValue.push(value)
            }
        } else {
            holderValue.push(value)
        }
        data.value = {...data.value, [key]: holderValue}
    }

    const handleTimeChange = (key, value) => {
        let timeValue = value
        console.log(timeValue)
        if (value === '24:00') {
            timeValue = '23:59'
        }
        data.value = {...data.value, [key]: timeValue}
    }

    const handleOnDelete = async () => {
        const toPostData = {...data.value, hospitalId: currentHospital?.id}
        const res = await deleteByIdAdditionalNotifyMethod(toPostData)
        if (res === HTTP_STATUS_OK) {
            const {additionalNotifyMethodKeys} = QueryKeyManager(
                QueryKeyTypes.additionalNotifyMethod,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: additionalNotifyMethodKeys.pattern(),
            })
            handleHideForm()
        }
    }
    const footerContent = (
        <div className="grid">
            {data.value?.id && (
                <Button
                    className="col p-button-danger p-button-text  h-3rem font-bold"
                    label="DELETE"
                    icon="pi pi-times"
                    onClick={() => handleOnDelete()}
                />
            )}

            <Button
                className="col h-3rem font-bold"
                label="SUBMIT"
                icon="pi pi-check"
                onClick={() => handleOnSubmit()}
            />
        </div>
    )

    const handleAddPhoneOrEmail = (key, value) => {
        let holderList = []
        if (key === additionalNotifyMethodKeys.phoneInput) {
            if (!value) {
                return showNotification(
                    severityNotification.error,
                    'Phone number cannot be empty!',
                )
            }
            if (value.length < 11) {
                return showNotification(
                    severityNotification.error,
                    'Phone invalid',
                )
            }

            if (
                isArrayAndNotEmpty(
                    data.value[additionalNotifyMethodKeys.phoneNumber],
                )
            ) {
                holderList = [
                    ...data.value[additionalNotifyMethodKeys.phoneNumber],
                ]

                let isMatch = false
                holderList.forEach(item => {
                    if (item.phone === value) {
                        return (isMatch = true)
                    }
                })
                if (isMatch) {
                    return showNotification(
                        severityNotification.warn,
                        'The phone number is already in use',
                    )
                }
            }

            let item = {phone: value, active: true}
            holderList.push(item)
            data.value = {
                ...data.value,
                [additionalNotifyMethodKeys.phoneNumber]: holderList,
                [key]: '44',
            }
        }
        if (key === additionalNotifyMethodKeys.emailInput) {
            if (!value) {
                return showNotification(
                    severityNotification.warn,
                    'Email cannot be empty!',
                )
            }

            const emailRegex =
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

            const isValid = emailRegex.test(value)
            if (!isValid) {
                return showNotification(
                    severityNotification.warn,
                    'Email invalid',
                )
            }

            if (
                isArrayAndNotEmpty(data.value[additionalNotifyMethodKeys.email])
            ) {
                holderList = [...data.value[additionalNotifyMethodKeys.email]]

                let isMatch = false
                holderList.forEach(item => {
                    if (item.email === value) {
                        return (isMatch = true)
                    }
                })
                if (isMatch) {
                    return showNotification(
                        severityNotification.warn,
                        'The email is already in use',
                    )
                }
            }

            let item = {email: value, active: true}
            holderList.push(item)
            data.value = {
                ...data.value,
                [additionalNotifyMethodKeys.email]: holderList,
                [key]: '',
            }
        }
    }

    const handleActiveOrDeleteItem = (key, idx, isActive = true, value) => {
        let holderList = isArrayAndNotEmpty(data.value[key])
            ? [...data.value[key]]
            : []

        if (holderList.length < 1) {
            return
        }

        if (isActive) {
            holderList[idx].active = value
        } else {
            holderList = holderList.filter((x, i) => idx !== i)
        }

        data.value = {...data.value, [key]: holderList}
    }

    return (
        <Dialog
            visible={visible.value}
            footer={footerContent}
            header="Additional Notification Methods Form"
            onHide={handleHideForm}>
            <div className="grid">
                {/* Name */}
                <div className="col-12">
                    {labelTemplate(
                        convertKeyToLabel(additionalNotifyMethodKeys.name),
                        true,
                    )}
                    <InputText
                        value={data.value[additionalNotifyMethodKeys.name]}
                        className="col-12 h-3rem"
                        placeholder="Input name"
                        onChange={e =>
                            (data.value = {
                                ...data.value,
                                [additionalNotifyMethodKeys.name]:
                                    e.target.value,
                            })
                        }
                    />
                </div>
                {/* Description */}
                <div className="col-12">
                    {labelTemplate(
                        convertKeyToLabel(
                            additionalNotifyMethodKeys.description,
                        ),
                    )}

                    <InputText
                        value={
                            data.value[additionalNotifyMethodKeys.description]
                        }
                        className="col-12 h-3rem"
                        placeholder="Input description"
                        onChange={e =>
                            (data.value = {
                                ...data.value,
                                [additionalNotifyMethodKeys.description]:
                                    e.target.value,
                            })
                        }
                    />
                </div>
                <Divider type="dashed" />
                {/* Day */}
                <div className="col-12">
                    {labelTemplate(
                        convertKeyToLabel(additionalNotifyMethodKeys.day),
                    )}
                    <div className="grid">
                        {weekDayOption.map((option, idx) => (
                            <div
                                key={`date-option-${option.value}`}
                                className="col m-0 p-0">
                                <CheckboxButton
                                    label={option.label}
                                    checked={
                                        isArrayAndNotEmpty(
                                            data.value[
                                                additionalNotifyMethodKeys.day
                                            ],
                                        )
                                            ? data.value[
                                                  additionalNotifyMethodKeys.day
                                              ].includes(option.value)
                                            : false
                                    }
                                    onClick={() =>
                                        handleOnChangeOtherMethods(
                                            additionalNotifyMethodKeys.day,
                                            option.value,
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>
                {/* Time */}
                <div className="col-12">
                    {labelTemplate(
                        convertKeyToLabel(additionalNotifyMethodKeys.time),
                        true,
                    )}
                    <div className="flex">
                        {notifyTimeOption.map((option, opIdx) => (
                            <React.Fragment key={`time-option-${option.value}`}>
                                <CheckboxButton
                                    label={option.label}
                                    checked={
                                        data.value[
                                            additionalNotifyMethodKeys.time
                                        ]
                                            ? data.value[
                                                  additionalNotifyMethodKeys
                                                      .time
                                              ] === option.value
                                            : false
                                    }
                                    onClick={() =>
                                        (data.value = {
                                            ...data.value,
                                            [additionalNotifyMethodKeys.time]:
                                                option.value,
                                        })
                                    }
                                />
                            </React.Fragment>
                        ))}
                    </div>
                    <i>
                        *The time will be calculated based on the client's time
                        zone. The client's current time zone is{' '}
                        <span className="text-darkcyan font-bold">
                            {timezoneId} GMT{' '}
                            {moment().tz(timezoneId).format('Z')}
                        </span>
                    </i>
                </div>
                {data.value[additionalNotifyMethodKeys.time] ===
                    timeOptionKeys.customTime && (
                    <React.Fragment>
                        {' '}
                        <div className="col">
                            {labelTemplate(
                                convertKeyToLabel(
                                    additionalNotifyMethodKeys.startTime,
                                ),
                                true,
                            )}
                            <div className="flex">
                                <Button
                                    className="border-noround-right px-4"
                                    icon={'pi pi-clock'}
                                />
                                <InputText
                                    className="col h-3rem border-noround-left"
                                    value={
                                        data.value[
                                            additionalNotifyMethodKeys.startTime
                                        ]
                                    }
                                    type="time"
                                    onChange={e =>
                                        handleTimeChange(
                                            additionalNotifyMethodKeys.startTime,
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="col">
                            {labelTemplate(
                                convertKeyToLabel(
                                    additionalNotifyMethodKeys.endTime,
                                ),
                                true,
                            )}
                            <div className="flex">
                                <Button
                                    className="border-noround-right px-4"
                                    icon={'pi pi-clock'}
                                />
                                <InputText
                                    className="col h-3rem border-noround-left"
                                    value={
                                        data.value[
                                            additionalNotifyMethodKeys.endTime
                                        ]
                                    }
                                    type="time"
                                    onChange={e =>
                                        handleTimeChange(
                                            additionalNotifyMethodKeys.endTime,
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                            <i>
                                *00:00 corresponds to{' '}
                                <span className="text-darkcyan font-bold">
                                    01:00 AM the next day.
                                </span>
                            </i>
                        </div>{' '}
                    </React.Fragment>
                )}
                {/* Method */}
                <div className="col-12">
                    {labelTemplate(
                        convertKeyToLabel(
                            additionalNotifyMethodKeys.notifyMethodType,
                        ),
                        true,
                    )}
                    <div className="flex">
                        {notifyMethodOptions.map((option, opIdx) => (
                            <React.Fragment
                                key={`method-option-${option.value}`}>
                                <CheckboxButton
                                    label={option.label}
                                    checked={
                                        isArrayAndNotEmpty(
                                            data.value[
                                                additionalNotifyMethodKeys
                                                    .notifyMethodType
                                            ],
                                        )
                                            ? data.value[
                                                  additionalNotifyMethodKeys
                                                      .notifyMethodType
                                              ].includes(option.value)
                                            : false
                                    }
                                    onClick={() =>
                                        handleOnChangeOtherMethods(
                                            additionalNotifyMethodKeys.notifyMethodType,
                                            option.value,
                                        )
                                    }
                                />
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                {isArrayAndNotEmpty(
                    data.value[additionalNotifyMethodKeys.notifyMethodType],
                ) &&
                    data.value[
                        additionalNotifyMethodKeys.notifyMethodType
                    ].includes('sms') && (
                        <div className="col">
                            {labelTemplate('Add phone number')}
                            <div className="flex">
                                <Button
                                    className="border-noround-right px-4"
                                    icon="pi pi-envelope"
                                />
                                <PhoneInput
                                    className={`p-0 font-semibold`}
                                    inputClass={
                                        'font-semibold  border-noround-left border-noround-right'
                                    }
                                    enableSearch
                                    country={'gb'}
                                    preferredCountries={[
                                        'gb',
                                        'us',
                                        'ca',
                                        'vn',
                                        'bg',
                                    ]}
                                    onKeyDown={({key}) =>
                                        key === 'Enter' &&
                                        handleAddPhoneOrEmail(
                                            additionalNotifyMethodKeys.phoneInput,
                                            data.value[
                                                additionalNotifyMethodKeys
                                                    .phoneInput
                                            ],
                                        )
                                    }
                                    inputStyle={{
                                        minHeight: '40px',
                                    }}
                                    alwaysDefaultMask={true}
                                    placeholder="Enter the phone number whose you want to send the SMS notification to."
                                    onChange={(
                                        value,
                                        country,
                                        e,
                                        formattedData,
                                    ) => {
                                        data.value = {
                                            ...data.value,
                                            [additionalNotifyMethodKeys.phoneInput]:
                                                formattedData,
                                        }
                                    }}
                                    value={
                                        data.value[
                                            additionalNotifyMethodKeys
                                                .phoneInput
                                        ]
                                    }
                                />
                                <Button
                                    className="border-noround-left px-4"
                                    icon="pi pi-plus"
                                    onClick={() =>
                                        handleAddPhoneOrEmail(
                                            additionalNotifyMethodKeys.phoneInput,
                                            data.value[
                                                additionalNotifyMethodKeys
                                                    .phoneInput
                                            ],
                                        )
                                    }
                                />
                            </div>
                            <h5 className="font-bold mb-0">
                                {' '}
                                Current recipient phone number list:
                            </h5>

                            {isArrayAndNotEmpty(
                                data.value[
                                    additionalNotifyMethodKeys.phoneNumber
                                ],
                            ) ? (
                                <div>
                                    <h6 className="font-bold mb-1 text-right px-3">
                                        {' '}
                                        Is use ?
                                    </h6>
                                    {data.value[
                                        additionalNotifyMethodKeys.phoneNumber
                                    ].map((phoneItem, phoneIdx) => (
                                        <div
                                            key={`phone${phoneIdx}`}
                                            className="flex align-items-center mb-1">
                                            <Button
                                                className="border-noround-right h-3rem w-3rem opacity-100"
                                                disabled={true}
                                                label={`${phoneIdx + 1}`}
                                            />{' '}
                                            <InputText
                                                className="col h-3rem font-semibold opacity-100 border-noround-left border-noround-right"
                                                value={phoneItem.phone}
                                                disabled
                                            />
                                            <Button
                                                className="border-noround-left h-3rem w-3rem "
                                                icon="pi pi-times"
                                                onClick={() =>
                                                    handleActiveOrDeleteItem(
                                                        additionalNotifyMethodKeys.phoneNumber,
                                                        phoneIdx,
                                                        false,
                                                    )
                                                }
                                            />
                                            <div className="px-3 ">
                                                <InputSwitch
                                                    checked={phoneItem.active}
                                                    onChange={e =>
                                                        handleActiveOrDeleteItem(
                                                            additionalNotifyMethodKeys.phoneNumber,
                                                            phoneIdx,
                                                            true,
                                                            e.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mb-0 mt-1">
                                    There are currently no recipient phone
                                    number added.
                                </p>
                            )}
                        </div>
                    )}
                {isArrayAndNotEmpty(
                    data.value[additionalNotifyMethodKeys.notifyMethodType],
                ) &&
                    data.value[
                        additionalNotifyMethodKeys.notifyMethodType
                    ].includes('email') && (
                        <div className="col">
                            {labelTemplate('Add email ')}
                            <div className="flex">
                                <Button
                                    className="border-noround-right px-4"
                                    icon="pi pi-at"
                                />
                                <InputText
                                    value={
                                        data.value[
                                            additionalNotifyMethodKeys
                                                .emailInput
                                        ]
                                    }
                                    keyfilter={/^[^<>*!/^|?,+= ]+$/}
                                    className="w-full h-3rem border-noround-left border-noround-right "
                                    placeholder="Input email"
                                    onChange={e =>
                                        (data.value = {
                                            ...data.value,
                                            [additionalNotifyMethodKeys.emailInput]:
                                                e.target.value,
                                        })
                                    }
                                    onKeyPress={({key}) =>
                                        key === 'Enter' &&
                                        handleAddPhoneOrEmail(
                                            additionalNotifyMethodKeys.emailInput,
                                            data.value[
                                                additionalNotifyMethodKeys
                                                    .emailInput
                                            ],
                                        )
                                    }
                                />
                                <Button
                                    className="border-noround-left px-4"
                                    icon="pi pi-plus"
                                    onClick={() =>
                                        handleAddPhoneOrEmail(
                                            additionalNotifyMethodKeys.emailInput,
                                            data.value[
                                                additionalNotifyMethodKeys
                                                    .emailInput
                                            ],
                                        )
                                    }
                                />
                            </div>
                            <h5 className="font-bold mb-0">
                                {' '}
                                Current recipient email list:
                            </h5>

                            {isArrayAndNotEmpty(
                                data.value[additionalNotifyMethodKeys.email],
                            ) ? (
                                <div>
                                    <h6 className="font-bold mb-1 text-right px-3">
                                        {' '}
                                        Is use ?
                                    </h6>
                                    {data.value[
                                        additionalNotifyMethodKeys.email
                                    ].map((emailItem, emailIdx) => (
                                        <div
                                            key={`phone${emailIdx}`}
                                            className="flex align-items-center mb-1">
                                            <Button
                                                className="border-noround-right h-3rem w-3rem opacity-100"
                                                disabled={true}
                                                label={`${emailIdx + 1}`}
                                            />{' '}
                                            <InputText
                                                className="col h-3rem font-semibold opacity-100 border-noround-left border-noround-right"
                                                value={emailItem.email}
                                                disabled
                                            />
                                            <Button
                                                className="border-noround-left h-3rem w-3rem "
                                                icon="pi pi-times"
                                                onClick={() =>
                                                    handleActiveOrDeleteItem(
                                                        additionalNotifyMethodKeys.email,
                                                        emailIdx,
                                                        false,
                                                    )
                                                }
                                            />
                                            <div className="px-3 ">
                                                <InputSwitch
                                                    checked={emailItem.active}
                                                    onChange={e =>
                                                        handleActiveOrDeleteItem(
                                                            additionalNotifyMethodKeys.email,
                                                            emailIdx,
                                                            true,
                                                            e.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mb-0 mt-1">
                                    There are currently no recipient phone
                                    number added.
                                </p>
                            )}
                        </div>
                    )}
            </div>
        </Dialog>
    )
})

export default StyledNotifyMethodsForm
